import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SlotDto, UserProfile } from '@motoschool/shared';
import { SLOT_STATUS_LABELS } from '@motoschool/shared';
import { api, ApiRequestError } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import { Avatar, Badge, Button, ErrorNotice, Field, Select, Sheet, Textarea } from './ui';
import { formatDateTime, formatTime } from '../lib/format';

interface SlotDetailSheetProps {
  slot: SlotDto | null;
  onClose: () => void;
}

interface StudentsResponse {
  items: Array<UserProfile & { balanceMinutes: number | null }>;
}

export function SlotDetailSheet({ slot, onClose }: SlotDetailSheetProps): React.JSX.Element | null {
  const { hasRole, timeZone } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [studentId, setStudentId] = useState('');

  const isStaff = hasRole('TEACHER', 'ADMIN');

  const { data: students } = useQuery({
    queryKey: ['users', 'students'],
    queryFn: () => api.get<StudentsResponse>('/users', { role: 'STUDENT', pageSize: 100 }),
    enabled: isStaff && slot !== null,
  });

  const invalidate = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['slots'] }),
      queryClient.invalidateQueries({ queryKey: ['requests'] }),
    ]);
  };

  const handleError = (caught: unknown): void => {
    setError(caught instanceof ApiRequestError ? caught.message : 'Не удалось выполнить действие');
  };

  const complete = useMutation({
    mutationFn: (status: 'COMPLETED' | 'NO_SHOW') =>
      api.post(`/slots/${slot?.id}/complete`, {
        status,
        chargeHours: true,
        comment: comment || null,
      }),
    onSuccess: async () => {
      await invalidate();
      onClose();
    },
    onError: handleError,
  });

  const cancel = useMutation({
    mutationFn: () => api.post(`/slots/${slot?.id}/cancel`, { reason: comment || null }),
    onSuccess: async () => {
      await invalidate();
      onClose();
    },
    onError: handleError,
  });

  const assign = useMutation({
    mutationFn: () => api.patch(`/slots/${slot?.id}`, { studentId: studentId || null }),
    onSuccess: async () => {
      await invalidate();
      onClose();
    },
    onError: handleError,
  });

  const requestBooking = useMutation({
    mutationFn: () => api.post('/requests', { slotId: slot?.id, comment: comment || null }),
    onSuccess: async () => {
      await invalidate();
      onClose();
    },
    onError: handleError,
  });

  if (!slot) return null;

  const isPast = new Date(slot.startsAt).getTime() < Date.now();
  const isClosed = slot.status === 'COMPLETED' || slot.status === 'NO_SHOW' || slot.status === 'CANCELLED';

  return (
    <Sheet isOpen title="Занятие" onClose={onClose}>
      <div className="space-y-5">
        <div>
          <div className="flex items-center justify-between gap-3">
            <p className="text-lg font-semibold tabular-nums">
              {formatTime(slot.startsAt, timeZone)} — {formatTime(slot.endsAt, timeZone)}
            </p>
            <Badge>{SLOT_STATUS_LABELS[slot.status]}</Badge>
          </div>
          <p className="mt-1 text-sm text-muted">{formatDateTime(slot.startsAt, timeZone)}</p>
          {slot.location && <p className="mt-1 text-sm text-muted">📍 {slot.location}</p>}
        </div>

        <div className="flex items-center gap-2 rounded-xl bg-canvas p-3">
          <Avatar name={slot.teacher.name} size="sm" />
          <div>
            <p className="text-sm font-medium">{slot.teacher.name}</p>
            <p className="text-xs text-muted">Инструктор</p>
          </div>
        </div>

        {slot.student && (
          <div className="flex items-center gap-2 rounded-xl bg-canvas p-3">
            <Avatar name={slot.student.name} size="sm" />
            <div>
              <p className="text-sm font-medium">{slot.student.name}</p>
              <p className="text-xs text-muted">{slot.student.phone ?? 'Ученик'}</p>
            </div>
          </div>
        )}

        {error && <ErrorNotice message={error} />}

        {/* --- действия ученика --- */}
        {!isStaff && slot.status === 'OPEN' && !isPast && (
          <div className="space-y-3">
            <Field label="Комментарий инструктору">
              <Textarea
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                placeholder="Что хотите отработать на занятии"
              />
            </Field>
            <Button fullWidth isLoading={requestBooking.isPending} onClick={() => requestBooking.mutate()}>
              Записаться на это время
            </Button>
            <p className="text-center text-xs text-muted">
              Инструктор подтвердит запись, вы получите уведомление
            </p>
          </div>
        )}

        {/* --- действия инструктора --- */}
        {isStaff && !isClosed && (
          <div className="space-y-4">
            <Field label="Ученик" hint="Можно назначить вручную, без заявки">
              <Select value={studentId || slot.student?.id || ''} onChange={(event) => setStudentId(event.target.value)}>
                <option value="">Не назначен</option>
                {students?.items.map((student) => (
                  <option key={student.id} value={student.id}>
                    {student.name}
                  </option>
                ))}
              </Select>
            </Field>

            <Button
              variant="secondary"
              fullWidth
              isLoading={assign.isPending}
              onClick={() => assign.mutate()}
            >
              Сохранить ученика
            </Button>

            <Field label="Комментарий">
              <Textarea
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                placeholder="Причина отмены или заметка по занятию"
              />
            </Field>

            {slot.student && (
              <div className="grid grid-cols-2 gap-2">
                <Button isLoading={complete.isPending} onClick={() => complete.mutate('COMPLETED')}>
                  Провёл занятие
                </Button>
                <Button
                  variant="secondary"
                  isLoading={complete.isPending}
                  onClick={() => complete.mutate('NO_SHOW')}
                >
                  Не пришёл
                </Button>
              </div>
            )}

            <Button variant="danger" fullWidth isLoading={cancel.isPending} onClick={() => cancel.mutate()}>
              Отменить занятие
            </Button>
          </div>
        )}

        {isClosed && (
          <p className="rounded-xl bg-canvas px-3 py-2 text-sm text-muted">
            Занятие закрыто, изменить его нельзя. Часы уже учтены в балансе ученика.
          </p>
        )}
      </div>
    </Sheet>
  );
}
